<template>
  <b-modal
    id="modal-outbound-dialer"
    :title="modalTitle"
    centered
    size="lg"
    scrollable
    no-close-on-backdrop
    body-class="dialer-modal-body"
    @hide="onModalHide"
    @show="onModalShow"
    hide-footer
  >
    <!-- ── STEP 1: Dial ───────────────────────────────────────────────── -->
    <div v-if="step === 'dial'">
      <b-alert v-if="!callsAvailable" show variant="warning" class="mb-1 py-50 small">
        {{ callingPausedMessage }}
      </b-alert>
      <b-form-group label="Enter Phone Number" label-for="dialer-phone">
        <b-form-input
          id="dialer-phone"
          v-model="phoneNumber"
          type="tel"
          maxlength="10"
          placeholder="10-digit mobile number"
          :state="phoneValid"
          @keyup.enter="searchInAts"
        />
        <b-form-invalid-feedback>Please enter a valid 10-digit number.</b-form-invalid-feedback>
      </b-form-group>

      <!-- Search in ATS button -->
      <div class="d-flex justify-content-end mt-1">
        <b-button
          variant="primary"
          :disabled="!phoneValid || isSearching"
          @click="searchInAts"
        >
          <b-spinner v-if="isSearching" small class="mr-50" />
          <span>{{ isSearching ? 'Searching\u2026' : 'Search in ATS' }}</span>
        </b-button>
      </div>

      <!-- Recent dials -->
      <div v-if="recentDials.length > 0" class="mt-1">
        <div class="d-flex align-items-center mb-50" style="gap: 6px;">
          <feather-icon icon="ClockIcon" size="14" class="text-muted" />
          <small class="text-muted font-weight-bold text-uppercase">Recent</small>
        </div>
        <div
          v-for="(item, idx) in recentDials"
          :key="item.phoneNumber + '_' + idx"
          class="d-flex justify-content-between align-items-center py-25 px-50 recent-dial-item"
        >
          <div @click="dialFromRecent(item)" style="cursor: pointer; flex: 1;">
            <small class="d-block font-weight-semibold">{{ item.phoneNumber }}</small>
            <small class="text-muted">{{ item.candidateName || 'Unknown' }}</small>
          </div>
          <b-button variant="flat-success" size="sm" class="p-25" @click="dialFromRecent(item)">
            <feather-icon icon="PhoneIcon" size="13" />
          </b-button>
        </div>
      </div>
    </div>

    <!-- ── STEP 2a: Candidate found ───────────────────────────────────── -->
    <div v-else-if="step === 'found'">

      <!-- ① Candidate card -->
      <b-card no-body border-variant="primary" class="mb-1 dialer-section">
        <b-card-body class="py-75 px-1">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1 min-w-0">
              <h6 class="mb-25 font-weight-bold">
            {{ foundCandidate.first_name }} {{ foundCandidate.last_name }}
              </h6>
              <p class="mb-25 text-muted small">
                <span v-if="foundCandidate.curr_position">{{ foundCandidate.curr_position }}</span>
                <span v-if="foundCandidate.curr_position && foundCandidate.curr_company"> @ </span>
                <span v-if="foundCandidate.curr_company">{{ foundCandidate.curr_company }}</span>
                <span v-if="!foundCandidate.curr_position && !foundCandidate.curr_company">—</span>
              </p>
              <p v-if="foundCandidate.email" class="mb-25 small text-muted">
                <feather-icon icon="MailIcon" size="12" class="mr-25" />{{ foundCandidate.email }}
              </p>
              <b-badge :variant="statusVariant(currentJobRecord && currentJobRecord.candidateStatus ? currentJobRecord.candidateStatus : foundCandidate.status)" class="mt-25">
                {{ currentJobRecord && currentJobRecord.candidateStatus ? currentJobRecord.candidateStatus : (foundCandidate.status || 'Unknown') }}
              </b-badge>
            </div>
            <b-button variant="flat-primary" size="sm" class="p-25 flex-shrink-0" style="line-height:1;" @click="viewCandidateProfile">
              <feather-icon icon="ExternalLinkIcon" size="13" />
              <span class="ml-25 text-11">View Profile</span>
            </b-button>
          </div>

          <!-- Quick facts strip -->
          <div class="dialer-quick-facts mt-75">
            <div v-if="foundCandidate.exp > 0" class="dialer-fact-item">
              <feather-icon icon="ClockIcon" size="11" class="mr-25 text-muted" />
              <span>{{ foundCandidate.exp }}{{ foundCandidate.relevant_exp ? ' (' + foundCandidate.relevant_exp + ' rel.)' : '' }} yrs</span>
            </div>
            <div v-if="foundCandidate.ctc > 0" class="dialer-fact-item">
              <feather-icon icon="DollarSignIcon" size="11" class="mr-25 text-muted" />
              <span>CTC: ₹{{ foundCandidate.ctc }}L</span>
            </div>
            <div v-if="foundCandidate.exp_ctc" class="dialer-fact-item">
              <feather-icon icon="TrendingUpIcon" size="11" class="mr-25 text-muted" />
              <span>Exp: ₹{{ foundCandidate.exp_ctc }}L</span>
            </div>
            <div v-if="foundCandidate.notice_period" class="dialer-fact-item">
              <feather-icon icon="CalendarIcon" size="11" class="mr-25 text-muted" />
              <span>{{ foundCandidate.notice_period }}d notice</span>
            </div>
            <div v-if="foundCandidate.curr_location" class="dialer-fact-item">
              <feather-icon icon="MapPinIcon" size="11" class="mr-25 text-muted" />
              <span>{{ foundCandidate.curr_location }}</span>
            </div>
            <div v-if="foundCandidate.qualification" class="dialer-fact-item">
              <feather-icon icon="BookIcon" size="11" class="mr-25 text-muted" />
              <span>{{ foundCandidate.qualification }}</span>
            </div>
          </div>
          <p v-if="foundCandidate.skills" class="mb-0 mt-50 small text-muted text-11">
            <feather-icon icon="ZapIcon" size="11" class="mr-25" />{{ foundCandidate.skills }}
          </p>
        </b-card-body>
      </b-card>

      <!-- ② Associated Jobs -->
      <b-card no-body class="mb-1 dialer-section">
        <b-card-header class="dialer-section-header py-50 px-1">
          <feather-icon icon="BriefcaseIcon" size="13" class="mr-50" />
          <span>Associated Jobs</span>
          <b-spinner v-if="isLoadingJobs" small class="ml-50 spinner-xs" />
        </b-card-header>
        <b-card-body class="p-0">
          <p v-if="!isLoadingJobs && candidateJobs.length === 0" class="text-muted small px-1 py-75 mb-0">
            No job records found.
          </p>
          <div v-else-if="candidateJobs.length > 0" style="max-height: 180px; overflow-y: auto;">
          <b-list-group flush>
            <b-list-group-item
              v-for="job in candidateJobs"
              :key="job.id"
              class="py-75 px-1"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="min-w-0">
                  <a href="#" class="small font-weight-semibold" @click.prevent="viewJob(job.job_id || job.id)">
                    {{ job.position_name || '—' }}
                  </a>
                  <small class="d-block text-muted">{{ job.job_id || ('#' + job.id) }}</small>
                  <small class="d-flex align-items-center flex-wrap mt-25" style="gap:6px;color:#6e6b7b;">
                    <span v-if="job.rec_name || job.recruiter">
                      <feather-icon icon="UserIcon" size="10" class="mr-25" />{{ job.rec_name || recruiterName(job.recruiter) }}
                    </span>
                    <span v-if="job.createdAt || job.assigned_at">
                      <feather-icon icon="ClockIcon" size="10" class="mr-25" />{{ daysAgoLabel(job.createdAt || job.assigned_at) }}
                    </span>
                  </small>
                </div>
                <b-badge :variant="statusVariant(job.candidateStatus)" size="sm" class="badge-status-truncate flex-shrink-0 ml-50">
                  {{ job.candidateStatus || 'Unknown' }}
          </b-badge>
              </div>
            </b-list-group-item>
          </b-list-group>
          </div>
        </b-card-body>
      </b-card>

      <!-- ③ Call History -->
      <b-card no-body class="mb-1 dialer-section">
        <b-card-header class="dialer-section-header d-flex align-items-center py-50 px-1">
          <feather-icon icon="PhoneCallIcon" size="13" class="mr-50" />
          <span>Call History (last 60 days)</span>
          <b-spinner v-if="isLoadingLogs" small class="ml-50 spinner-xs" />
          <b-button
            v-if="phoneNumber"
            variant="link"
            size="sm"
            class="ml-auto p-0 x-small"
            @click="viewCallHistoryInMyCalls"
          >
            View in Call Analytics
          </b-button>
        </b-card-header>
        <b-card-body class="p-0">
          <p v-if="!isLoadingLogs && callLogs.length === 0" class="text-muted small px-1 py-75 mb-0">
            No previous calls for this number.
          </p>
          <div v-else-if="callLogs.length > 0" style="max-height: 200px; overflow-y: auto;">
          <b-list-group flush>
            <b-list-group-item
              v-for="(log, idx) in callLogs"
              :key="log.Sid || log.sid || log.DateCreated || idx"
              class="d-flex justify-content-between align-items-center py-75 px-1"
            >
              <div>
                <small class="d-block font-weight-semibold">{{ log._dateLabel }}</small>
                <small class="text-muted">
                  <span v-if="log._durationLabel">{{ log._durationLabel }}</span>
                  <span v-if="log.recruiter || log.Recruiter">{{ log._durationLabel ? ' · ' : '' }}{{ log.recruiter || log.Recruiter }}</span>
                </small>
              </div>
              <b-badge :variant="callStatusVariant(log)" size="sm">
                {{ callStatusLabel(log) }}
              </b-badge>
            </b-list-group-item>
          </b-list-group>
          </div>
        </b-card-body>
      </b-card>

      <!-- Eligibility warning (wrong status/too recent) — informational only, does not block calling -->
      <b-alert v-if="!isInCurrentJob && !assignEligibility.canAssign" variant="danger" show class="py-75 px-1 mb-1">
        <feather-icon icon="XCircleIcon" class="mr-25" />
        <span class="small font-weight-bold">Cannot assign to this job</span>
        <p class="mb-0 small mt-25">{{ assignEligibility.reason }}</p>
      </b-alert>

      <!-- DID not assigned note -->
      <b-alert v-if="hasDID === false" variant="warning" show class="py-75 px-1 mb-1">
        <feather-icon icon="PhoneOffIcon" class="mr-25" />
        <span class="small font-weight-bold">No DID assigned</span>
        <p class="mb-0 small mt-25">Your account does not have an Exotel DID assigned. Please contact your administrator to enable calling.</p>
      </b-alert>

      <!-- Actions -->
      <div class="d-flex justify-content-between">
        <b-button variant="outline-secondary" size="sm" @click="step = 'dial'">Back</b-button>
        <b-button
          variant="success"
          :disabled="!canMakeCall || isCallInProgress"
          @click="doCall('found')"
        >
          <b-spinner v-if="isCallInProgress" small class="mr-50" />
          <feather-icon v-else icon="PhoneIcon" class="mr-50" />
          {{ isCallInProgress ? 'Calling…' : 'Call Now' }}
        </b-button>
      </div>
    </div>

    <!-- ── STEP 2b: Not found ─────────────────────────────────────────── -->
    <div v-else-if="step === 'not-found'">
      <b-alert variant="warning" show class="mb-1">
        <div class="alert-body">
          <feather-icon icon="UserXIcon" class="mr-50" />
          <strong>{{ phoneNumber }}</strong> is not in the ATS.
        </div>
      </b-alert>

      <!-- Call History -->
      <b-card no-body class="mb-1 dialer-section">
        <b-card-header class="dialer-section-header d-flex align-items-center py-50 px-1">
          <feather-icon icon="PhoneCallIcon" size="13" class="mr-50" />
          <span>Call History (last 60 days)</span>
          <b-spinner v-if="isLoadingLogs" small class="ml-50 spinner-xs" />
          <b-button
            v-if="phoneNumber"
            variant="link"
            size="sm"
            class="ml-auto p-0 x-small"
            @click="viewCallHistoryInMyCalls"
          >
            View in Call Analytics
          </b-button>
        </b-card-header>
        <b-card-body class="p-0">
          <p v-if="!isLoadingLogs && callLogs.length === 0" class="text-muted small px-1 py-75 mb-0">
            No previous calls for this number.
          </p>
          <div v-else-if="callLogs.length > 0" style="max-height: 200px; overflow-y: auto;">
          <b-list-group flush>
            <b-list-group-item
              v-for="(log, idx) in callLogs"
              :key="log.Sid || log.sid || log.DateCreated || idx"
              class="d-flex justify-content-between align-items-center py-75 px-1"
            >
              <div>
                <small class="d-block font-weight-semibold">{{ log._dateLabel }}</small>
                <small class="text-muted">
                  <span v-if="log._durationLabel">{{ log._durationLabel }}</span>
                  <span v-if="log.recruiter || log.Recruiter">{{ log._durationLabel ? ' · ' : '' }}{{ log.recruiter || log.Recruiter }}</span>
                </small>
              </div>
              <b-badge :variant="callStatusVariant(log)" size="sm">
                {{ callStatusLabel(log) }}
              </b-badge>
            </b-list-group-item>
          </b-list-group>
          </div>
        </b-card-body>
      </b-card>

      <!-- DID not assigned note -->
      <b-alert v-if="hasDID === false" variant="warning" show class="py-75 px-1 mb-1">
        <feather-icon icon="PhoneOffIcon" class="mr-25" />
        <span class="small font-weight-bold">No DID assigned</span>
        <p class="mb-0 small mt-25">Your account does not have an DID assigned. Please contact your administrator to enable calling.</p>
      </b-alert>

      <div class="d-flex justify-content-between">
        <b-button variant="outline-secondary" size="sm" @click="step = 'dial'">Back</b-button>
        <div class="d-flex d-gap-8">
          <b-button variant="outline-primary" size="sm" @click="skippedCall = true; step = 'post-call-not-found'">
            <feather-icon icon="UserPlusIcon" class="mr-25" />
            Add to ATS
        </b-button>
        <b-button
          variant="success"
          :disabled="!canMakeCall || isCallInProgress"
          @click="doCall('not-found')"
        >
          <b-spinner v-if="isCallInProgress" small class="mr-50" />
          <feather-icon v-else icon="PhoneIcon" class="mr-50" />
          {{ isCallInProgress ? 'Calling…' : 'Call Now' }}
        </b-button>
        </div>
      </div>
    </div>

    <!-- ── STEP 3a: Post-call, candidate known ───────────────────────── -->
    <div v-else-if="step === 'post-call-found'">
      <p class="text-success mb-1">
        <feather-icon icon="CheckCircleIcon" class="mr-50" />
        Call initiated for <strong>{{ foundCandidate.first_name }} {{ foundCandidate.last_name }}</strong>.
      </p>

      <!-- ④ Quick Edit panel -->
      <div class="border rounded p-2 mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <b-button variant="flat-primary" size="sm" class="p-0" @click="quickEditOpen = !quickEditOpen">
            <feather-icon icon="EditIcon" size="13" class="mr-50" />
            Quick Edit
          </b-button>
          <small class="text-muted">— syncs on Save Updates</small>
        </div>
        <b-collapse :visible="quickEditOpen">
          <b-row class="mt-1">
            <b-col md="12">
              <b-form-group label="Notes / Follow-up" label-for="qe-remarks">
                <b-form-textarea id="qe-remarks" v-model="foundCandidate.remarks" rows="3" placeholder="Jot down key points while on call…" />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end mt-50">
            <b-button size="sm" variant="primary" :disabled="isSavingQuickEdit" @click="saveQuickEdit">
              <b-spinner v-if="isSavingQuickEdit" small class="mr-50" />
              Save Updates
            </b-button>
          </div>
        </b-collapse>
      </div>

      <!-- Candidate NOT in this job's pipeline yet -->
      <template v-if="!isInCurrentJob">
        <!-- Blocked: hard-blocked or too recent -->
        <b-alert v-if="!assignEligibility.canAssign" variant="danger" show class="py-75 px-1 mb-1">
          <feather-icon icon="XCircleIcon" class="mr-25" />
          <span class="small font-weight-bold">Cannot assign to this job</span>
          <p class="mb-0 small mt-25">{{ assignEligibility.reason }}</p>
        </b-alert>

        <!-- Eligible: show assign button -->
        <b-alert v-else variant="warning" show class="py-75 px-1 mb-1">
          <div class="d-flex align-items-start justify-content-between d-gap-8">
            <div>
              <feather-icon icon="AlertCircleIcon" class="mr-25" />
              <span class="small font-weight-bold">Not assigned to this job yet</span>
              <p class="mb-0 small text-muted mt-25">
                <strong>{{ foundCandidate && (foundCandidate.first_name + ' ' + foundCandidate.last_name) }}</strong>
                has no record for <strong>{{ jobName }}</strong>. Assign them to unlock the status update.
              </p>
            </div>
            <b-button
              variant="warning"
              size="sm"
              class="flex-shrink-0"
              :disabled="isAssigning"
              @click="assignToCurrentJob"
            >
              <b-spinner v-if="isAssigning" small class="mr-50" />
              <feather-icon v-else icon="UserPlusIcon" class="mr-25" />
              {{ isAssigning ? 'Assigning…' : 'Assign to Job' }}
            </b-button>
          </div>
        </b-alert>
      </template>

      <!-- Candidate IS in this job — show status update -->
      <template v-if="isInCurrentJob">
      <b-alert v-if="!isFollowUpCall && selectedOutcomeBlocked" show variant="warning" class="py-75 px-1 mb-1">
        <feather-icon icon="LockIcon" class="mr-25" />
        <span class="small">{{ lockedStatusMessage }} Call notes can still be saved.</span>
      </b-alert>
      <b-form-group v-if="!isFollowUpCall" label-for="call-status-select" class="dialer-status-group">
        <template #label>
          Update Status <span class="text-danger">*</span>
        </template>
        <v-select
          id="call-status-select"
          v-model="postCallStatus"
          :options="callStatusOptions"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          placeholder="Select a call outcome…"
          :reduce="opt => opt.value"
          label="text"
          :class="['dialer-status-select', { 'is-invalid': statusTouched && !postCallStatus }]"
          :appendToBody="true"
          :calculatePosition="calculateDropdownPosition"
          @input="statusTouched = true"
        />
        <small v-if="statusTouched && !postCallStatus" class="text-danger">Please select a call outcome before saving.</small>
      </b-form-group>

        <!-- "Interested" → redirect to candidate edit to fill details & share profile -->
        <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Interested'" class="dialer-profile-share mt-1">
          <div class="d-flex align-items-center mb-50">
            <feather-icon icon="ZapIcon" size="14" class="mr-50 text-success" />
            <span class="small font-weight-bold text-success">Candidate is Interested!</span>
          </div>
          <p class="small text-muted mb-75">
            Fill in the candidate's details (CTC, experience, notice period, etc.) and share the profile with the client.
          </p>
          <div class="d-flex justify-content-end">
            <b-button size="sm" variant="success" @click="goToEditAndShare">
              <feather-icon icon="ExternalLinkIcon" size="12" class="mr-50" />
              Fill Details &amp; Share Profile
            </b-button>
          </div>
        </div>

        <!-- "Duplicate" → reason + cooling period (same as candidate profile flow) -->
        <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Duplicate'" class="dialer-duplicate-fields mt-1">
          <b-form-group label-for="dup-reason-select" class="mb-75">
            <template #label>
              Duplicate Reason <span class="text-danger">*</span>
            </template>
            <b-form-select
              id="dup-reason-select"
              v-model="dupReasonSelected"
              :options="duplicateSelectOptions"
              :state="dupReasonTouched && !dupReasonSelected ? false : null"
              class="dialer-form-select"
              @change="dupReasonTouched = true"
            />
            <small v-if="dupReasonTouched && !dupReasonSelected" class="text-danger">Please select a duplicate reason.</small>
      </b-form-group>
          <b-form-group label-for="cooling-timeline-select" class="mb-75">
            <template #label>
              Cooling Period <span class="text-danger">*</span>
            </template>
            <b-form-select
              id="cooling-timeline-select"
              v-model="coolingTimeline"
              :options="coolingTimelineSelectOptions"
              :state="coolingTimelineTouched && coolingTimeline == null ? false : null"
              class="dialer-form-select"
              @change="coolingTimelineTouched = true"
            />
            <small v-if="coolingTimelineTouched && coolingTimeline == null" class="text-danger">Please select a cooling period.</small>
          </b-form-group>
          <p v-if="computedDuplicateDate" class="mb-75 small text-muted">
            Duplicate date: {{ computedDuplicateDate }}
          </p>
          <p v-if="existingDuplicateDate" class="mb-0 small text-muted">
            Existing duplicate date on record: {{ existingDuplicateDate }}
          </p>
        </div>

        <!-- "Not Interested" → reason (same as candidate profile flow) -->
        <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Not Interested'" class="dialer-not-interested-fields mt-1">
          <b-form-group label-for="ni-reason-select" class="mb-75">
            <template #label>
              Not Interested Reason <span class="text-danger">*</span>
            </template>
            <b-form-select
              id="ni-reason-select"
              v-model="notInterestedReasonSelected"
              :options="notInterestedReasonSelectOptions"
              :state="notInterestedReasonTouched && !notInterestedReasonSelected ? false : null"
              class="dialer-form-select"
              @change="notInterestedReasonTouched = true"
            />
            <small v-if="notInterestedReasonTouched && !notInterestedReasonSelected" class="text-danger">Please select a not interested reason.</small>
          </b-form-group>
        </div>

      <div v-if="isFollowUpCall || selectedOutcomeBlocked || statusWriteVerdict.reason === 'same'" class="d-flex justify-content-end mt-1">
        <b-button
          variant="primary"
          :disabled="isSaving"
          @click="saveNotesAndClose"
        >
          <b-spinner v-if="isSaving" small class="mr-50" />
          {{ hasDialerNotes || isFollowUpCall ? 'Save notes & Close' : 'Close' }}
        </b-button>
      </div>
      <div v-else-if="postCallStatus !== 'Interested'" class="d-flex justify-content-end mt-1">
        <b-button
          variant="primary"
          :disabled="isSaving"
          @click="onSaveAndCloseClick"
        >
          <b-spinner v-if="isSaving" small class="mr-50" />
          Save &amp; Close
        </b-button>
      </div>
      </template>

    </div>

    <!-- ── STEP 3b: Post-call, candidate unknown ─────────────────────── -->
    <div v-else-if="step === 'post-call-not-found'">
      <!-- Only shown when a real call was made -->
      <template v-if="!skippedCall">
      <p class="text-success mb-1">
        <feather-icon icon="CheckCircleIcon" class="mr-50" />
        Call initiated to <strong>{{ phoneNumber }}</strong>.
      </p>

      </template>

      <!-- Call outcome selector — only relevant when a call was made -->
      <b-form-group v-if="!skippedCall" label="Did the call connect?" label-for="call-outcome">
        <div class="d-flex d-gap-8">
          <b-button
            :variant="callConnected === true ? 'success' : 'outline-success'"
            size="sm"
            @click="callConnected = true"
          >
            <feather-icon icon="PhoneCallIcon" class="mr-25" /> Yes, Connected
          </b-button>
          <b-button
            :variant="callConnected === false ? 'danger' : 'outline-danger'"
            size="sm"
            @click="callConnected = false"
          >
            <feather-icon icon="PhoneOffIcon" class="mr-25" /> Not Answered
          </b-button>
        </div>
      </b-form-group>

      <!-- If not answered: just close (Exotel webhook auto-logs the attempt) -->
      <div v-if="callConnected === false && !wantsToAddAfterNoAnswer" class="mt-1">
        <p class="text-muted small mb-1">
          <feather-icon icon="InfoIcon" class="mr-25" />
          This call attempt is automatically recorded in the Exotel call log. You can view it in the Productivity dashboard.
        </p>
        <div class="d-flex justify-content-between align-items-center">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="wantsToAddAfterNoAnswer = true"
          >
            <feather-icon icon="UserPlusIcon" class="mr-25" />
            Have candidate details? Add to ATS anyway
          </b-button>
          <b-button variant="outline-secondary" @click="closeModal">Close</b-button>
        </div>
      </div>

      <!-- Add to ATS section — when connected, skipped, or recruiter opts in after no answer -->
      <div v-if="callConnected === true || skippedCall || wantsToAddAfterNoAnswer" class="mt-1">
        <b-card no-body class="border mb-1">
          <b-card-header class="dialer-section-header py-75 px-1">
            <feather-icon icon="UserPlusIcon" class="mr-50 text-primary" />
            <span class="font-weight-bold">Add to ATS</span>
          </b-card-header>
          <div class="p-1">
            <b-form-group label-for="dialer-paste-new" class="mb-75">
              <textarea
                id="dialer-paste-new"
                class="form-control"
                rows="1"
                placeholder="Paste candidate JSON data here to auto-fill…"
                @paste="handleCandidatePaste"
              ></textarea>
              <small class="text-muted">Paste JSON to auto-fill the fields below.</small>
            </b-form-group>

      <b-row>
        <b-col md="6">
                <b-form-group label="First Name *" label-for="new-first-name" :invalid-feedback="addErrors.firstName" :state="addErrors.firstName ? false : null">
                  <b-form-input id="new-first-name" v-model="newCandidate.firstName" trim placeholder="First name" :state="addErrors.firstName ? false : null" @input="addErrors.firstName = ''" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Last Name *" label-for="new-last-name" :invalid-feedback="addErrors.lastName" :state="addErrors.lastName ? false : null">
                  <b-form-input id="new-last-name" v-model="newCandidate.lastName" trim placeholder="Last name" :state="addErrors.lastName ? false : null" @input="addErrors.lastName = ''" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Current Job Title *" label-for="new-job-title" :invalid-feedback="addErrors.jobTitle" :state="addErrors.jobTitle ? false : null">
                  <b-form-input id="new-job-title" v-model="newCandidate.jobTitle" trim placeholder="e.g. Sales Executive (or Fresher)" :state="addErrors.jobTitle ? false : null" @input="addErrors.jobTitle = ''" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Current Company *" label-for="new-company" :invalid-feedback="addErrors.companyName" :state="addErrors.companyName ? false : null">
                  <b-form-input id="new-company" v-model="newCandidate.companyName" trim placeholder="Company name (or Fresher)" :state="addErrors.companyName ? false : null" @input="addErrors.companyName = ''" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Email *" label-for="new-email" :invalid-feedback="addErrors.email" :state="addErrors.email ? false : null">
                  <b-form-input id="new-email" v-model="newCandidate.email" trim type="email" placeholder="email@example.com" :state="addErrors.email ? false : null" @input="addErrors.email = ''" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Phone *" label-for="new-phone" :invalid-feedback="addErrors.contact" :state="addErrors.contact ? false : null">
                  <b-form-input id="new-phone" :value="newCandidate.contact" readonly />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Current CTC (LPA)" label-for="new-ctc">
                  <b-form-input id="new-ctc" v-model="newCandidate.ctc" trim type="number" placeholder="e.g. 3.5" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Experience (yrs)" label-for="new-exp">
                  <b-form-input id="new-exp" v-model="newCandidate.exp" trim type="number" placeholder="e.g. 2" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Current City" label-for="new-city">
                  <v-select
                    id="new-city"
                    v-model="newCandidate.selectedCity"
                    :options="cities"
                    label="title"
                    :reduce="opt => opt.title"
                    placeholder="Search city…"
                    taggable
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
                <b-form-group label="Skills" label-for="new-skills">
                  <b-form-input id="new-skills" :value="newCandidate.skillsSelected" readonly plaintext />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Gender" label-for="new-gender">
                  <v-select
                    id="new-gender"
                    v-model="newCandidate.genderSelected"
                    :options="genderOptions"
                    placeholder="Select gender"
                    :reduce="opt => opt.title"
                    label="title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
                <b-form-group label="Qualification" label-for="new-qual">
                  <v-select
                    id="new-qual"
                    v-model="newCandidate.qualification"
                    :options="qualifications"
                    label="title"
                    :reduce="opt => opt.title"
                    placeholder="Select qualification…"
                    taggable
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
                <b-form-group label="Source" label-for="new-source">
                  <v-select
                    id="new-source"
                    v-model="newCandidate.source"
                    :options="sourceOptions"
                    placeholder="How was candidate found?"
                    label="title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
                <b-form-group label="Expected CTC (LPA)" label-for="new-ectc">
                  <b-form-input id="new-ectc" v-model="newCandidate.expectedCTC" trim type="number" placeholder="e.g. 5" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Date of Birth" label-for="new-dob">
                  <b-form-input id="new-dob" v-model="newCandidate.dob" type="date" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group :label="isAddToAtsResumeRequired ? 'Resume / CV *' : 'Resume / CV'" label-for="new-cv">
                  <b-form-file
                    id="new-cv"
                    v-model="newCandidate.cvFile"
                    accept=".pdf,.doc,.docx"
                    placeholder="Choose file…"
                    browse-text="Browse"
                  />
                  <small class="text-muted">PDF or Word, max 1.5 MB</small>
                  <small v-if="isAddToAtsResumeRequired" class="text-muted d-block">Resume is required.</small>
                  <small v-else-if="isInfraCollarJob" class="text-muted d-block">Resume optional while adding to ATS.</small>
          </b-form-group>
        </b-col>
      </b-row>

            <div class="d-flex justify-content-end mt-50">
        <b-button
          variant="primary"
                :disabled="!newCandidate.firstName.trim() || isSaving"
          @click="addToAtsAndClose"
        >
          <b-spinner v-if="isSaving" small class="mr-50" />
                <feather-icon v-else icon="UserPlusIcon" class="mr-25" />
          Add to ATS &amp; Close
        </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormInvalidFeedback,
  BFormTextarea,
  BButton,
  BSpinner,
  BCard,
  BCardBody,
  BCardHeader,
  BBadge,
  BAlert,
  BRow,
  BCol,
  BListGroup,
  BListGroupItem,
  BFormFile,
  BCollapse,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import AuthService from '@/services/auth-header'
import adminConfigService from '@/services/admin-config-service'
import candidateService from '@/services/candidate-service'
import jobService from '@/services/job-service'
import userService from '@/services/user-service'
import { toLocalIndianDigits, getExotelCallErrorMessage } from '@/utils/exotel-call-helpers'
import {
  buildCallNotesOnlyPayload,
  evaluateCallSurfaceStatusWrite,
  resolveJobPipelineStatus,
} from '@/utils/call-outcome-helpers'
import { isFollowUpPipelineStatus, statusFlowContextFromJob } from '@/utils/candidate-status-flow'
import {
  loadAgentAvailability,
  getCallsAvailableOrDefault,
  onAvailabilityChange,
  CALLS_UNAVAILABLE_MESSAGE,
} from '@/utils/exotel-agent-availability'
import moment from 'moment'

// Module-level session cache — survives modal open/close but resets on page refresh.
// Holds calls initiated in this browser session so they appear immediately in history
// before Exotel's 15-min webhook delay writes them to the DB.
const SESSION_INITIATED_CALLS = []

const EXOTEL_CALLS_API = (() => {
  const fromEnv = process.env.VUE_APP_API
  if (fromEnv && typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv.endsWith('/') ? fromEnv : `${fromEnv}/`
  }
  return 'https://ats.sattvahuman.in/api/'
})()

// Parses a DB timestamp to a JS Date correctly in IST.
// created_at  → "2026-06-26 16:15:18.389 +0530"  (IST, explicit offset) — preferred
// DateCreated → "2026-06-26 10:45:18"             (UTC, no offset, Exotel API time)
function parseISTDate(str) {
  if (!str) return null
  // Normalize: collapse the date/time space separator → T
  let s = String(str).trim().replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:)/, '$1T$2')
  // Collapse the space between seconds and timezone offset: "18.389 +0530" → "18.389+0530"
  s = s.replace(/(\d)\s+([+-]\d{4})$/, '$1$2')
  // Add colon to bare numeric offset "+0530" → "+05:30"
  s = s.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3')
  // If the string now carries timezone info, parse directly
  if (/[Zz]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s)
  // No timezone → DateCreated is UTC; append Z so JS treats it as UTC
  return new Date(s + 'Z')
}

export default {
  name: 'OutboundCallModal',
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormInvalidFeedback,
    BFormTextarea,
    BButton,
    BSpinner,
    BCard,
    BCardBody,
    BCardHeader,
    BBadge,
    BAlert,
    BRow,
    BCol,
    BListGroup,
    BListGroupItem,
    BFormFile,
    BCollapse,
    vSelect,
  },
  props: {
    jobId: {
      type: [String, Number],
      default: null,
    },
    jobName: {
      type: String,
      default: '',
    },
    jobCategory: {
      type: String,
      default: '',
    },
    jobVertical: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      exotelConfig: null,
      callsAvailable: true,
      _availabilityUnsub: null,
      step: 'dial',
      phoneNumber: '',
      _pendingAutoSearch: false,
      isSearching: false,
      isCallInProgress: false,
      isSaving: false,
      isSavingQuickEdit: false,
      quickEditOpen: false,
      statusTouched: false,
      isAssigning: false,
      skippedCall: false,
      wantsToAddAfterNoAnswer: false,
      addErrors: { firstName: '', lastName: '', jobTitle: '', companyName: '', email: '', contact: '' },
      foundCandidate: null,
      postCallStatus: null,
      dupReasonSelected: null,
      coolingTimeline: null,
      dupReasonTouched: false,
      coolingTimelineTouched: false,
      notInterestedReasonSelected: null,
      notInterestedReasonTouched: false,
      notInterestedReasonSelectOptions: [
        { value: null, text: 'Select not interested reason…', disabled: true },
        { value: 'Not Looking for Change', text: 'Not Looking for Change' },
        { value: 'Not ok with the Client/Company', text: 'Not ok with the Client/Company' },
        { value: 'Not ok with Location', text: 'Not ok with Location' },
        { value: 'Not ok with Shift', text: 'Not ok with Shift' },
        { value: 'Not ok with Compensation', text: 'Not ok with Compensation' },
        { value: 'Not ok with Designation/Role', text: 'Not ok with Designation/Role' },
        { value: 'Recently Changed', text: 'Recently Changed' },
        { value: 'Already Interviewed/Duplicate', text: 'Already Interviewed/Duplicate' },
        { value: 'Already working with Organization', text: 'Already working with Organization' },
        { value: 'Re-Hire', text: 'Re-Hire' },
        { value: 'Ex-Employee', text: 'Ex-Employee' },
        { value: 'Other', text: 'Other' },
      ],
      duplicateSelectOptions: [
        { value: null, text: 'Select duplicate reason…', disabled: true },
        { value: 'Other Vendor Source Lock', text: 'Other Vendor Source Lock' },
        { value: 'Profile in Cooling Period', text: 'Profile in Cooling Period' },
      ],
      coolingTimelineSelectOptions: [
        { value: null, text: 'Select cooling period…', disabled: true },
        { value: 0, text: '0' },
        { value: 30, text: '1 Month' },
        { value: 60, text: '2 Months' },
        { value: 90, text: '3 Months' },
        { value: 120, text: '4 Months' },
        { value: 150, text: '5 Months' },
        { value: 180, text: '6 Months' },
      ],
      newCandidate: {
      firstName: '',
      lastName: '',
      email: '',
        contact: '',
      companyName: '',
        jobTitle: '',
        selectedCity: '',
        ctc: '',
        exp: '',
        skillsSelected: '',
        qualification: '',
        dob: null,
        genderSelected: '',
        source: { title: 'ATS' },
        expectedCTC: '',
        cvFile: null,
      },
      sourceOptions: [
        { title: 'ATS' },
        { title: 'Naukri' },
        { title: 'LinkedIn' },
        { title: 'Reference' },
        { title: 'Walk-In' },
        { title: 'Campus' },
        { title: 'Job Portal' },
        { title: 'Other' },
      ],
      genderOptions: [
        { title: 'Male' },
        { title: 'Female' },
        { title: 'LGBQ' },
        { title: 'Transgender' },
      ],
      cities: [{"title":"Mumbai"},{"title":"Delhi"},{"title":"Bangalore"},{"title":"Hyderabad"},{"title":"Pune"},{"title":"Ahmedabad"},{"title":"Chennai"},{"title":"Kolkata"},{"title":"Surat"},{"title":"Jaipur"},{"title":"Lucknow"},{"title":"Nagpur"},{"title":"Indore"},{"title":"Thane"},{"title":"Bhopal"},{"title":"Navi Mumbai"},{"title":"Noida"},{"title":"Gurgaon"},{"title":"Chandigarh"},{"title":"Coimbatore"},{"title":"Kochi"},{"title":"Vadodara"},{"title":"Nashik"},{"title":"Rajkot"},{"title":"Aurangabad"},{"title":"Patna"},{"title":"Ludhiana"},{"title":"Agra"},{"title":"Faridabad"},{"title":"Meerut"},{"title":"Ghaziabad"},{"title":"Remote"},{"title":"PAN INDIA"}],
      qualifications: [],
      recruiterMap: {}, // emp_id → full name lookup
      callConnected: null,
      callLogs: [],
      isLoadingLogs: false,
      candidateJobs: [],
      isLoadingJobs: false,
      sessionCalls: [],
      callStatusOptions: [
      { value: 'Call Back', text: 'Call Back' },
      { value: 'Not Answered', text: 'Not Answered' },
      { value: 'Not Reachable', text: 'Not Reachable' },
      { value: 'Wrong Number', text: 'Wrong Number' },
      { value: 'Not Interested', text: 'Not Interested' },
      { value: 'Not Relevant', text: 'Not Relevant' },
        { value: 'Duplicate', text: 'Duplicate' },
      { value: 'Interested', text: 'Interested' },
      ],
    }
  },
  watch: {
    step(val) {
      if (val === 'post-call-not-found') {
        if (!this.newCandidate.contact && this.phoneNumber) {
          this.newCandidate.contact = this.phoneNumber
        }
        if (!this.newCandidate.skillsSelected && this.jobName) {
          this.newCandidate.skillsSelected = this.jobName
        }
      }
    },
  },
  computed: {
    currentUser() {
      try { return JSON.parse(localStorage.getItem('userData') || '{}') } catch { return {} }
    },
    modalTitle() {
      return this.jobName ? `Dialer \u2014 ${this.jobName}` : 'Outbound Dialer'
    },
    phoneValid() {
      const v = toLocalIndianDigits(this.phoneNumber)
      if (!v) {
        return String(this.phoneNumber || '').trim().length === 0 ? null : false
      }
      return /^[6-9]\d{9}$/.test(v) ? true : false
    },
    canMakeCall() {
      const userData = this.currentUser
      const currentUserEmpId = userData && userData.emp_id
      // Use String coercion so numeric vs string emp_id never causes a false-negative
      const hasExotelPermission = this.exotelConfig && Array.isArray(this.exotelConfig)
        ? this.exotelConfig.some(c => String(c.emp_id) === String(currentUserEmpId))
        : false
      const hasCreatePermission = this.$can ? this.$can('create', 'exotel_call') : false
      // Fallback: if Exotel config hasn't loaded yet but the user is logged in, allow calling
      const configNotLoaded = !this.exotelConfig || !this.exotelConfig.length
      const hasPermission = hasExotelPermission || hasCreatePermission || (configNotLoaded && !!currentUserEmpId)
      return hasPermission && this.callsAvailable !== false
    },
    callingPausedMessage() {
      return CALLS_UNAVAILABLE_MESSAGE
    },
    isInCurrentJob() {
      if (!this.candidateJobs || !this.jobId) return false
      return this.candidateJobs.some(j => String(j.id) === String(this.jobId) || String(j.job_id) === String(this.jobId))
    },
    assignEligibility() {
      if (this.isInCurrentJob) return { canAssign: true, reason: null }
      if (!this.candidateJobs.length) return { canAssign: true, reason: null }
      return this.checkAssignEligibility()
    },
    currentJobRecord() {
      if (!this.candidateJobs || !this.jobId) return null
      return this.candidateJobs.find(j => String(j.id) === String(this.jobId) || String(j.job_id) === String(this.jobId)) || null
    },
    currentPipelineStatus() {
      return resolveJobPipelineStatus(
        this.currentJobRecord && this.currentJobRecord.candidateStatus,
        this.foundCandidate && this.foundCandidate.status,
      )
    },
    statusFlowContext() {
      return statusFlowContextFromJob(this.currentJobRecord, {
        category: this.jobCategory,
        vertical: this.jobVertical,
        client: (this.currentJobRecord && (this.currentJobRecord.client || this.currentJobRecord.client_name)) || '',
      })
    },
    statusWriteVerdict() {
      return evaluateCallSurfaceStatusWrite(
        this.currentPipelineStatus,
        this.postCallStatus,
        this.statusFlowContext,
      )
    },
    selectedOutcomeBlocked() {
      return this.statusWriteVerdict.reason === 'blocked'
    },
    selectedOutcomeAllowed() {
      return this.statusWriteVerdict.changeStatus
    },
    isFollowUpCall() {
      return isFollowUpPipelineStatus(this.currentPipelineStatus)
    },
    lockedStatusMessage() {
      return this.statusWriteVerdict.message
        || `Cannot change status from ${this.currentPipelineStatus || 'this status'} (status flow).`
    },
    hasDialerNotes() {
      return Boolean(this.foundCandidate && String(this.foundCandidate.remarks || '').trim())
    },
    hasDID() {
      if (!this.exotelConfig || !Array.isArray(this.exotelConfig) || !this.exotelConfig.length) return null // still loading
      const userData = this.currentUser
      const empId = userData && userData.emp_id
      return this.exotelConfig.some(c => String(c.emp_id) === String(empId))
    },
    recentDials() {
      return this.sessionCalls.slice().reverse().slice(0, 5)
    },
    isInfraCollarJob() {
      const category = this.jobCategory || ''
      const vertical = this.jobVertical || ''
      return vertical === 'Infra Industry' && (category === 'Blue Collar' || category === 'White Collar')
    },
    isAddToAtsResumeRequired() {
      const category = this.jobCategory || ''
      const vertical = this.jobVertical || ''

      // Infra Industry Blue or White Collar — optional on Add to ATS
      if (vertical === 'Infra Industry' && (category === 'Blue Collar' || category === 'White Collar')) return false

      if (category === 'Service Industry') return true
      if (category === 'White Collar') return true

      return false
    },
    existingDuplicateDate() {
      const rec = this.currentJobRecord
      if (!rec || !rec.duplicate_date) return null
      return rec.duplicate_date
    },
    computedDuplicateDate() {
      return this.computeDuplicateDateFromCooling()
    },
  },
  methods: {
    calculateDropdownPosition(dropdownList, component, { width, top, left }) {
      const triggerRect = component.$refs.toggle.getBoundingClientRect()
      const scrollX = window.scrollX || window.pageXOffset
      const scrollY = window.scrollY || window.pageYOffset
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top
      const maxHeight = 220

      dropdownList.style.width = width || `${triggerRect.width}px`
      dropdownList.style.left = left || `${scrollX + triggerRect.left}px`
      dropdownList.style.maxHeight = `${maxHeight}px`
      dropdownList.style.overflowY = 'auto'
      dropdownList.style.zIndex = '1060'

      const dropdownHeight = Math.min(dropdownList.scrollHeight, maxHeight)
      if (spaceAbove > spaceBelow && spaceAbove >= dropdownHeight) {
        dropdownList.style.top = `${scrollY + triggerRect.top - dropdownHeight}px`
      } else {
        dropdownList.style.top = top || `${scrollY + triggerRect.bottom}px`
      }
    },

    checkAssignEligibility() {
      const HARD_BLOCK = ['ScreenSelect', 'Shortlisted', 'Joined', 'Offered', 'Selected', 'OfferAccepted', 'OfferDeclined', 'Offer Accepted']
      const TIME_SENSITIVE = ['UploadedBySourcer', 'UploadedByRecruiter', 'AssignedByMassMail', 'AssignedByJobPost', 'AssignedByATSSourcer', 'Initiated']
      const PROFILE_SHARED_DAYS = 30

      for (const job of this.candidateJobs) {
        if (String(job.id) === String(this.jobId) || String(job.job_id) === String(this.jobId)) continue
        const status = (job.candidateStatus || '').trim()
        if (HARD_BLOCK.some(s => s.toLowerCase() === status.toLowerCase())) {
          return { canAssign: false, reason: `Candidate is actively in process (status: "${status}") in job ${job.job_id || ('#' + job.id)}.` }
        }
        const isProfileShared = status.toLowerCase() === 'profileshared'
        const threshold = isProfileShared ? PROFILE_SHARED_DAYS : 7
        if (isProfileShared || TIME_SENSITIVE.some(s => s.toLowerCase() === status.toLowerCase())) {
          const added = job.createdAt || job.assigned_at || job.updatedAt
          // If no date is available we cannot determine how long ago the status was set,
          // so we allow the assignment rather than blocking on incomplete data.
          if (added) {
            const daysSince = (Date.now() - new Date(added).getTime()) / (1000 * 60 * 60 * 24)
            if (daysSince < threshold) {
              const daysLeft = Math.ceil(threshold - daysSince)
              return {
                canAssign: false,
                reason: `Candidate has status "${status}" in job ${job.job_id || ('#' + job.id)} (${Math.floor(daysSince)} day(s) ago). Eligible in ${daysLeft} more day(s).`,
              }
            }
          }
        }
      }
      return { canAssign: true, reason: null }
    },

    dialFromRecent(item) {
      this.phoneNumber = toLocalIndianDigits(item.phoneNumber) || item.phoneNumber
      this.searchInAts()
    },

    /** Normalize candidate/exist API envelope — OK + empty array means not in ATS. */
    parseCandidateExistResponse(res) {
      if (!res) return null
      if (res.status === 400 || res.status === 404) return null
      const body = (res.data && typeof res.data === 'object' && ('status' in res.data || 'data' in res.data))
        ? res.data
        : res
      if (!body || body.status !== 'OK') return null
      const data = body.data
      if (Array.isArray(data)) {
        return data.length > 0 ? data[0] : null
      }
      if (data && typeof data === 'object' && (data.id || data.candidate_id)) {
        return data
      }
      return null
    },

    normalizeFoundCandidate(raw) {
      return {
        ...raw,
        status: typeof raw.status === 'string'
          ? raw.status
          : (raw.status && raw.status.name) || raw.candidateStatus || '',
        curr_position: typeof raw.curr_position === 'string'
          ? raw.curr_position
          : (raw.curr_position && (raw.curr_position.name || raw.curr_position.title)) || '',
        curr_company: typeof raw.curr_company === 'string'
          ? raw.curr_company
          : (raw.curr_company && raw.curr_company.name) || '',
      }
    },

    async searchInAts() {
      const normalizedPhone = toLocalIndianDigits(this.phoneNumber)
      if (!normalizedPhone || !/^[6-9]\d{9}$/.test(normalizedPhone)) return
      this.phoneNumber = normalizedPhone
      this.isSearching = true
      try {
        const res = await candidateService.checkCandidateExist('', normalizedPhone)
        const raw = this.parseCandidateExistResponse(res)
        if (raw) {
          this.foundCandidate = this.normalizeFoundCandidate(raw)
          this.step = 'found'
          await this.fetchCandidateJobs(raw.id || raw.candidate_id)
        } else {
          this.foundCandidate = null
          this.step = 'not-found'
        }
      } catch (e) {
        console.error('ATS lookup failed', e)
        this.foundCandidate = null
        this.step = 'not-found'
      } finally {
        this.isSearching = false
      }
      await this.fetchCallLogs()
    },

    statusVariant(status) {
      if (!status) return 'secondary'
      const map = {
        // Upload / assign statuses
        UploadedBySourcer: 'secondary',
        UploadedByRecruiter: 'secondary',
        AssignedByMassMail: 'secondary',
        AssignedByJobPost: 'secondary',
        AssignedByATSSourcer: 'secondary',
        MassMail: 'secondary',
        Initiated: 'info',
        // Progress statuses
        ProfileShared: 'info',
        ScreenSelect: 'info',
        Shortlisted: 'success',
        Selected: 'success',
        Offered: 'primary',
        OfferAccepted: 'primary',
        Joined: 'success',
        // Negative statuses
        NotOffered: 'secondary',
        OfferDeclined: 'secondary',
        'Not Interested': 'danger',
        'Not Relevant': 'danger',
        'Not Answered': 'warning',
        'Not Reachable': 'warning',
        'Wrong Number': 'warning',
        'Call Back': 'warning',
        Rejected: 'danger',
      }
      return map[status] || 'secondary'
    },

    async loadExotelConfig() {
      try {
        const response = await adminConfigService.getExotelConfig()
        if (response && response.status === 'OK') {
          this.exotelConfig = response.data
        }
      } catch (e) {
        console.error('Exotel config error', e)
      }
    },

    async refreshCallingAvailability() {
      try {
        const row = await loadAgentAvailability({ force: true })
        this.callsAvailable = row ? row.calls_available !== false : getCallsAvailableOrDefault()
      } catch (e) {
        this.callsAvailable = true
      }
    },

    getCallerIdForCurrentUser() {
      if (!this.exotelConfig) return null
      const userData = this.currentUser
      const userConfig = this.exotelConfig.find(c => String(c.emp_id) === String(userData && userData.emp_id))
      return userConfig ? userConfig.callerId : null
    },

    async doCall(context) {
      this.isCallInProgress = true
      try {
        const userData = this.currentUser
        const callerId = this.getCallerIdForCurrentUser() || '09484956746'
        const callData = {
          from: '91' + ((userData && userData.contact_no) || ''),
          to: '91' + String(this.phoneNumber).replace(/^0+/, ''),
          callerId,
          source: 'induslabs',
          options: {
            callType: 'trans',
            timeLimit: 2000,
            timeOut: 30,
            record: true,
            recordingChannels: 'dual',
            recordingFormat: 'mp3',
            customField: JSON.stringify({
              candidate_id: context === 'found' && this.foundCandidate ? this.foundCandidate.id : null,
              candidate_name:
                context === 'found' && this.foundCandidate
                  ? `${this.foundCandidate.first_name || ''} ${this.foundCandidate.last_name || ''}`.trim()
                  : null,
              job_id: this.jobId,
              recruiter: (userData && userData.emp_id) || 'Unknown',
            }),
            statusCallback: 'https://ats.sattvahuman.in/api/exotel/webhook',
            statusCallbackEvents: ['terminal', 'answered'],
            statusCallbackContentType: 'application/json',
          },
        }
        const callRes = await adminConfigService.makeExotelCall(callData)

        // makeExotelCall catches its own errors and returns err.response (with numeric .status)
        // A successful response returns res.data (no numeric .status at top level)
        if (!callRes || (typeof callRes.status === 'number' && callRes.status >= 400)) {
          const errMsg = getExotelCallErrorMessage(callRes)
          this.$bvToast.toast(errMsg, { title: 'Call Failed', variant: 'danger', solid: true, autoHideDelay: 6000 })
          return
        }

        // Record optimistically so it shows in call history before the webhook fires
        const callEntry = {
          _pending: true,
          phoneNumber: this.phoneNumber,
          candidateName: context === 'found' && this.foundCandidate
            ? `${this.foundCandidate.first_name || ''} ${this.foundCandidate.last_name || ''}`.trim()
            : null,
          recruiter: (userData && userData.emp_id) || '',
          DateCreated: new Date().toISOString(),
          Status: 'initiated',
          Duration: null,
        }
        SESSION_INITIATED_CALLS.push(callEntry)
        this.sessionCalls.push(callEntry)

        this.$bvToast.toast(`Call initiated to ${this.phoneNumber}. Fill in the details below.`, {
          title: 'Call Initiated', variant: 'success', solid: true, autoHideDelay: 4000,
        })
        this.skippedCall = false
        this.step = context === 'found' ? 'post-call-found' : 'post-call-not-found'
      } catch (e) {
        console.error('Call failed', e)
      } finally {
        this.isCallInProgress = false
      }
    },

    async assignToCurrentJob() {
      if (!this.foundCandidate || !this.jobId) return
      const eligibility = this.checkAssignEligibility()
      if (!eligibility.canAssign) {
        this.$bvToast.toast(eligibility.reason, { title: 'Cannot Assign', variant: 'danger', solid: true, autoHideDelay: 6000 })
        return
      }
      this.isAssigning = true
      try {
        const userData = this.currentUser
        const username = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
        const user = JSON.stringify([{ title: username, emp_id: userData.emp_id }])
        const payload = {
          job_id: this.jobId,
          candidate_id: [this.foundCandidate.id],
          candidateStatus: 'UploadedByRecruiter',
          source: 'ATS',
          addedBy: user,
          updatedBy: user,
          sourcer: userData.emp_id,
          jt: {
            title: `Candidate ${this.foundCandidate.id} assigned via Dialer`,
            description: 'Assigned via Outbound Dialer',
            type: 'success',
            createdBy: username,
            jobId: this.jobId,
            candidate_id: this.foundCandidate.id,
            candidateStatus: 'UploadedByRecruiter',
          },
        }
        const res = await jobService.assignCandidateToJob(payload)
        if (res && res.status === 'OK') {
          this.$bvToast.toast('Candidate assigned to this job. You can now update their status.', {
            title: 'Assigned!', variant: 'success', solid: true
          })
          await this.fetchCandidateJobs(this.foundCandidate.id)
        } else if (res && (res.status === 410 || (res.data && res.data.message && res.data.message.includes('already exist')))) {
          this.$bvToast.toast('Candidate is already in this job.', { title: 'Info', variant: 'info', solid: true })
          await this.fetchCandidateJobs(this.foundCandidate.id)
        } else {
          this.$bvToast.toast('Could not assign candidate. Please try again.', { title: 'Error', variant: 'danger', solid: true })
        }
      } catch (e) {
        console.error('Assign to job failed', e)
        this.$bvToast.toast('An error occurred during assignment.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.isAssigning = false
      }
    },

    goToEditAndShare() {
      if (!this.foundCandidate) return
      const candidateId = this.foundCandidate.id
      const jobId = this.jobId || ''

      this.$bvModal.hide('modal-outbound-dialer')
      this.resetModal()
      this.$router.push({
        name: 'apps-candidates-edit',
        params: { id: candidateId, jobId },
        query: {
          interested: 'YES',
          ...(this.jobCategory && { category: this.jobCategory }),
          ...(this.jobVertical && { vertical: this.jobVertical }),
        },
      })
    },

    async saveQuickEdit() {
      if (!this.foundCandidate) return
      this.isSavingQuickEdit = true
      try {
        const res = await candidateService.updateCandidate(this.foundCandidate.id, this.foundCandidate)
        if (res && res.status === 'OK') {
          this.$bvToast.toast('Candidate updated.', { title: 'Saved', variant: 'success', solid: true, autoHideDelay: 2500 })
        } else {
          this.$bvToast.toast('Could not save updates. Please try again.', { title: 'Error', variant: 'danger', solid: true })
        }
      } catch (e) {
        console.error('Quick edit save failed', e)
        this.$bvToast.toast('An error occurred. Please try again.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.isSavingQuickEdit = false
      }
    },

    onSaveAndCloseClick() {
      this.statusTouched = true
      if (!this.postCallStatus) return
      if (this.postCallStatus === 'Duplicate') {
        this.dupReasonTouched = true
        if (!this.dupReasonSelected) return
        this.coolingTimelineTouched = true
        if (this.coolingTimeline == null) return
      }
      if (this.postCallStatus === 'Not Interested') {
        this.notInterestedReasonTouched = true
        if (!this.notInterestedReasonSelected) return
      }
      this.saveAndClose()
    },

    computeDuplicateDateFromCooling() {
      if (this.coolingTimeline == null) return null
      // Same date format as MyCandidates flat-pickr (d M Y → e.g. "30 Jun 2026")
      return moment().add(this.coolingTimeline, 'days').format('D MMM YYYY')
    },

    buildDuplicateStatusPayload(userData) {
      const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
      const username = `${userData.first_name} ${userData.last_name}`
      const mgrId = userData.mgr_id
      const mgrName = (this.recruiterMap[String(mgrId)] || username).toUpperCase()
      const pMgrId = (this.currentJobRecord && this.currentJobRecord.p_mgr_id) || ''
      const duplicateDate = this.computeDuplicateDateFromCooling()
      return {
        candidateStatus: 'Duplicate',
        updatedBy: user,
        reason: `${this.dupReasonSelected}| Cooling Time: ${this.coolingTimeline} days`,
        duplicate_date: duplicateDate,
        recruiter: userData.emp_id,
        mgr_id: mgrId,
        p_mgr_id: pMgrId,
        'Manager Name': this.$can('read', 'talent_acquisition') ? mgrName : username.toUpperCase(),
        'Employee Name': username.toUpperCase(),
        extra_details: { callNotes: (this.foundCandidate && this.foundCandidate.remarks) || '' },
      }
    },

    buildNotInterestedStatusPayload(userData) {
      const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
      const username = `${userData.first_name} ${userData.last_name}`
      const mgrId = userData.mgr_id
      const mgrName = (this.recruiterMap[String(mgrId)] || username).toUpperCase()
      const pMgrId = (this.currentJobRecord && this.currentJobRecord.p_mgr_id) || ''
      return {
        candidateStatus: 'Not Interested',
        updatedBy: user,
        reason: this.notInterestedReasonSelected,
        comment: (this.foundCandidate && this.foundCandidate.remarks) || '',
        recruiter: userData.emp_id,
        mgr_id: mgrId,
        p_mgr_id: pMgrId,
        'Manager Name': this.$can('read', 'talent_acquisition') ? mgrName : username.toUpperCase(),
        'Employee Name': username.toUpperCase(),
      }
    },

    async saveNotesAndClose() {
      if (!this.foundCandidate || !this.isInCurrentJob) {
        this.resetModal()
        this.$bvModal.hide('modal-outbound-dialer')
        return
      }
      if (this.selectedOutcomeBlocked && !this.isFollowUpCall) {
        this.$bvToast.toast(this.lockedStatusMessage, {
          title: 'Status flow', variant: 'warning', solid: true, autoHideDelay: 5000,
        })
      }
      const notes = this.hasDialerNotes ? String(this.foundCandidate.remarks).trim() : ''
      if (!notes) {
        this.resetModal()
        this.$bvModal.hide('modal-outbound-dialer')
        return
      }
      this.isSaving = true
      try {
        const res = await candidateService.updateCandidateStatus(
          this.foundCandidate.id,
          this.jobId,
          buildCallNotesOnlyPayload({ userData: this.currentUser, callNotes: notes }),
        )
        if (res && res.status === 'OK') {
          this.$bvToast.toast('Call notes saved. Pipeline status was not changed.', {
            title: 'Notes saved', variant: 'success', solid: true, autoHideDelay: 3000,
          })
        } else {
          this.$bvToast.toast('Could not save call notes. Please try again.', {
            title: 'Error', variant: 'danger', solid: true,
          })
        }
      } catch (e) {
        console.error('Notes-only save failed', e)
        this.$bvToast.toast('An error occurred. Please try again.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.isSaving = false
        this.resetModal()
        this.$bvModal.hide('modal-outbound-dialer')
      }
    },

    async saveAndClose() {
      if (!this.postCallStatus || !this.foundCandidate) return
      if (this.postCallStatus === 'Duplicate' && (!this.dupReasonSelected || this.coolingTimeline == null)) return
      if (this.postCallStatus === 'Not Interested' && !this.notInterestedReasonSelected) return
      await this.refreshCandidateJobsSilent(this.foundCandidate.id)
      const verdict = evaluateCallSurfaceStatusWrite(
        this.currentPipelineStatus,
        this.postCallStatus,
        this.statusFlowContext,
      )
      if (!verdict.changeStatus) {
        await this.saveNotesAndClose()
        return
      }
      if (!this.isInCurrentJob) {
        this.$bvToast.toast(
          'This candidate is not in this job\'s pipeline. Please assign them first.',
          { title: 'Not Assigned to Job', variant: 'warning', solid: true }
        )
        return
      }
      this.isSaving = true
      try {
        const userData = this.currentUser
        let statusPayload
        if (this.postCallStatus === 'Duplicate') {
          statusPayload = this.buildDuplicateStatusPayload(userData)
        } else if (this.postCallStatus === 'Not Interested') {
          statusPayload = this.buildNotInterestedStatusPayload(userData)
        } else {
          // Same JSON-stringified-array shape `updatedBy` uses in
          // `buildDuplicateStatusPayload()`/`buildNotInterestedStatusPayload()`
          // above (NOT a bare emp_id string) — `CandidatesView.vue`'s
          // "Updated by" field unconditionally does
          // `JSON.parse(candidateData.updated_by)[0].title`, which throws
          // if `updated_by` isn't valid JSON. See the identical fix/comment
          // in `src/utils/call-outcome-helpers.js#buildCallOutcomeStatusPayload`.
          const username = `${(userData && userData.first_name) || ''} ${(userData && userData.last_name) || ''}`.trim()
          const user = `[{"title" : "${username}", "emp_id": "${(userData && userData.emp_id) || ''}"}]`
          statusPayload = {
            candidateStatus: this.postCallStatus,
            extra_details: { callNotes: (this.foundCandidate && this.foundCandidate.remarks) || '' },
            updatedBy: user,
          }
        }
        const res = await candidateService.updateCandidateStatus(
          this.foundCandidate.id,
          this.jobId,
          statusPayload,
        )
        if (res && res.status === 'OK') {
          this.$bvToast.toast('Status updated successfully.', { title: 'Saved', variant: 'success', solid: true, autoHideDelay: 2000 })
          this.$emit('refresh')
          this.resetModal()
          this.$bvModal.hide('modal-outbound-dialer')
        } else {
          this.$bvToast.toast('Could not update status. Please try again.', { title: 'Error', variant: 'danger', solid: true })
        }
      } catch (e) {
        console.error('Status update failed', e)
        this.$bvToast.toast('An error occurred. Please try again.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.isSaving = false
      }
    },

    handleCandidatePaste(e) {
      const clipboardData = e.clipboardData || window.clipboardData
      const pastedData = clipboardData.getData('Text')
      try {
        const jsonData = JSON.parse(pastedData)
        for (const key in jsonData) {
          if (Object.prototype.hasOwnProperty.call(this.newCandidate, key)) {
            this.newCandidate[key] = jsonData[key]
          }
        }
        if (jsonData.first_name)    this.newCandidate.firstName    = jsonData.first_name
        if (jsonData.last_name)     this.newCandidate.lastName     = jsonData.last_name
        if (jsonData.LastName)      this.newCandidate.lastName     = jsonData.LastName
        if (jsonData.contact_no)    this.newCandidate.contact      = jsonData.contact_no
        if (jsonData.curr_company)  this.newCandidate.companyName  = jsonData.curr_company
        if (jsonData.curr_position) this.newCandidate.jobTitle     = jsonData.curr_position
        if (jsonData.curr_location) this.newCandidate.selectedCity = jsonData.curr_location
        if (jsonData.job_title)     this.newCandidate.jobTitle     = jsonData.job_title
        e.preventDefault()
        this.$bvToast.toast('Fields filled from pasted data.', {
          title: 'Paste Applied', variant: 'success', solid: true, autoHideDelay: 2500,
        })
      } catch {
        // Not valid JSON — let the user type normally
      }
    },

    validateAddForm() {
      const e = { firstName: '', lastName: '', jobTitle: '', companyName: '', email: '', contact: '' }
      let valid = true
      if (!this.newCandidate.firstName.trim()) { e.firstName = 'First Name is mandatory'; valid = false }
      if (!this.newCandidate.lastName.trim()) { e.lastName = 'Last Name is mandatory'; valid = false }
      if (!this.newCandidate.companyName.trim()) { e.companyName = "Company is mandatory — type 'Fresher' if applicable"; valid = false }
      if (!this.newCandidate.jobTitle.trim()) { e.jobTitle = "Job Title is mandatory — type 'Fresher' if applicable"; valid = false }
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.newCandidate.email)
      if (!this.newCandidate.email || !emailOk) { e.email = 'Please enter a valid email address'; valid = false }
      const contact = String(this.newCandidate.contact || '').trim()
      if (!contact || contact.length !== 10 || !/^\d{10}$/.test(contact)) { e.contact = 'Phone must be exactly 10 digits'; valid = false }
      if (this.isAddToAtsResumeRequired && !this.newCandidate.cvFile) {
        this.$bvToast.toast('Please upload a resume for Service Industry / White Collar candidates.', { title: 'Resume Required', variant: 'warning', solid: true })
        valid = false
      }
      this.addErrors = e
      return valid
    },

    async addToAtsAndClose() {
      if (!this.validateAddForm()) return

      if (this.newCandidate.cvFile && this.newCandidate.cvFile.size > 1690000) {
        this.$bvToast.toast('Resume file must be under 1.5 MB.', { title: 'File Too Large', variant: 'warning', solid: true })
        return
      }

      this.isSaving = true
      try {
        // 'OK' = candidate exists (duplicate), 400 = fresh candidate (proceed)
        const dupCheck = await candidateService.checkCandidateExist(
          this.newCandidate.email.toLowerCase().trim(),
          this.newCandidate.contact.trim(),
        )
        if (dupCheck && (dupCheck.status === 'OK' || dupCheck.status === 200)) {
          this.addErrors = { ...this.addErrors, email: 'Candidate already exists in ATS' }
          this.isSaving = false
          return
        }

        const userData = this.currentUser
        const user = JSON.stringify([{ title: `${userData.first_name} ${userData.last_name}`, emp_id: userData.emp_id }])
        const formData = new FormData()
        formData.append('first_name',    this.newCandidate.firstName.trim())
        formData.append('last_name',     this.newCandidate.lastName.trim())
        formData.append('email',         this.newCandidate.email.toLowerCase().trim())
        formData.append('contact_no',    this.newCandidate.contact.trim())
        formData.append('curr_company',  this.newCandidate.companyName.trim())
        formData.append('curr_position', this.newCandidate.jobTitle.trim())
        formData.append('curr_location', (this.newCandidate.selectedCity || '').trim())
        const ctcVal = parseFloat(this.newCandidate.ctc)
        if (!isNaN(ctcVal)) formData.append('ctc', ctcVal)
        const expVal = parseFloat(this.newCandidate.exp)
        if (!isNaN(expVal)) formData.append('exp', expVal)
        formData.append('skills',        this.newCandidate.skillsSelected || this.jobName || '')
        formData.append('source',        this.newCandidate.source?.title || 'ATS')
        formData.append('status',        'UploadedBySourcer')
        formData.append('created_by',    user)
        formData.append('updated_by',    user)
        formData.append('qualification', this.newCandidate.qualification || '')
        if (this.newCandidate.dob) formData.append('dob', this.newCandidate.dob)
        formData.append('gender',        this.newCandidate.genderSelected || '')
        formData.append('cvFile',        this.newCandidate.cvFile || '')
        const ectcVal = parseFloat(this.newCandidate.expectedCTC)
        if (!isNaN(ectcVal)) formData.append('expected_ctc', ectcVal)
        if (this.jobId) formData.append('job_id', this.jobId)

        const res = await candidateService.addCandidate(formData)
        if (res && (res.status === 'OK' || res.status === 200)) {
          const newCandidateId = res.data?.id || res.id
          if (this.jobId && newCandidateId) {
            const username2 = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
            const user2 = JSON.stringify([{ title: username2, emp_id: userData.emp_id }])
            const assignPayload = {
              job_id: this.jobId,
              candidate_id: [newCandidateId],
              candidateStatus: 'UploadedByRecruiter',
              source: this.newCandidate.source?.title || 'ATS',
              addedBy: user2,
              updatedBy: user2,
              sourcer: userData.emp_id,
              jt: {
                title: `Candidate ${newCandidateId} added & assigned via Dialer`,
                description: 'Added and assigned via Outbound Dialer',
                type: 'success',
                createdBy: username2,
                jobId: this.jobId,
                candidate_id: newCandidateId,
                candidateStatus: 'UploadedByRecruiter',
              },
            }
            await jobService.assignCandidateToJob(assignPayload)
          }
          this.$bvToast.toast(`${this.newCandidate.firstName} added to ATS and assigned to this job.`, {
            title: 'Candidate Added', variant: 'success', solid: true,
          })
          this.$emit('refresh')
          this.resetModal()
          this.$bvModal.hide('modal-outbound-dialer')
        } else {
          const msg = res?.data?.message || res?.data?.error || 'Could not add candidate. Please try again.'
          this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
        }
      } catch (e) {
        console.error('Add candidate failed', e)
        this.$bvToast.toast('An error occurred. Please try again.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.isSaving = false
      }
    },

    async fetchCallLogs() {
      this.isLoadingLogs = true
      this.callLogs = []
      try {
        const normalizedSearch = toLocalIndianDigits(this.phoneNumber)
        if (!normalizedSearch) return

        const toDate = new Date()
        const fromDate = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
        const fmt = d => d.toISOString().split('T')[0]
        const url = `${EXOTEL_CALLS_API}exotel/calls?startDate=${fmt(fromDate)}&endDate=${fmt(toDate)}&phone=${encodeURIComponent(normalizedSearch)}&limit=10&page=1`
        const res = await axios.get(url, { headers: AuthService.authHeader() })

        let serverLogs = []
        if (res.data && res.data.data) {
          serverLogs = res.data.data.filter(call => {
            const num = call.To || call.to || call.From || call.from || ''
            return toLocalIndianDigits(num) === normalizedSearch
          })
        }

        // Prepend any calls initiated this session for this number that haven't
        // yet been written to the DB (Exotel webhook delay ~15 min).
        // Exclude a pending entry once a matching server record appears.
        const pendingForNumber = SESSION_INITIATED_CALLS.filter(c => {
          const normalized = toLocalIndianDigits(c.phoneNumber)
          if (normalized !== normalizedSearch) return false
          // Drop pending entry if a server record already exists within 20 min of it
          const initiatedAt = parseISTDate(c.DateCreated)?.getTime() ?? 0
          const alreadyInDB = serverLogs.some(s => {
            const serverAt = parseISTDate(s.created_at || s.DateCreated || s.dateCreated)?.getTime() ?? 0
            return Math.abs(serverAt - initiatedAt) < 20 * 60 * 1000
          })
          return !alreadyInDB
        })

        this.callLogs = [...pendingForNumber, ...serverLogs]
          .slice(0, 10)
          .map(log => ({
            ...log,
            _dateLabel: this.formatCallDate(log),
            _durationLabel: this.formatCallDuration(log),
          }))
      } catch (e) {
        console.error('Call logs fetch failed', e)
      } finally {
        this.isLoadingLogs = false
      }
    },

    async fetchCandidateJobs(candidateId) {
      this.isLoadingJobs = true
      this.candidateJobs = []
      try {
        await this.refreshCandidateJobsSilent(candidateId)
      } finally {
        this.isLoadingJobs = false
      }
    },

    async refreshCandidateJobsSilent(candidateId) {
      if (!candidateId) return
      try {
        const res = await candidateService.getCandidateViewData(candidateId)
        if (res && res.status === 'OK' && res.data && res.data.candidateJobs) {
          this.candidateJobs = res.data.candidateJobs
        }
      } catch (e) {
        console.error('Candidate jobs fetch failed', e)
      }
    },

    recruiterName(empId) {
      if (!empId) return null
      return this.recruiterMap[String(empId)] || empId
    },

    daysAgoLabel(dateStr) {
      if (!dateStr) return ''
      const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24))
      if (days === 0) return 'today'
      if (days === 1) return '1 day ago'
      return `${days} days ago`
    },

    formatCallDate(log) {
      const raw = log.created_at || log.DateCreated || log.dateCreated || log.StartTime || log.start_time
      if (!raw) return '—'
      try {
        const d = parseISTDate(raw)
        return d ? d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }) : String(raw)
      } catch (e) {
        return raw
      }
    },

    formatCallDuration(log) {
      // Connected/talk-time duration comes from the raw Exotel webhook payload
      // (answer_duration excludes ring time). Fall back to the legacy
      // root-level field for older records that predate raw_payload being stored.
      let rawPayload = log.raw_payload || {}
      if (typeof rawPayload === 'string') {
        try { rawPayload = JSON.parse(rawPayload) } catch { rawPayload = {} }
      }
      const secs = parseInt(rawPayload.answer_duration ?? log.Duration ?? log.duration ?? 0)
      if (!secs) return ''
      const m = Math.floor(secs / 60)
      const s = secs % 60
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },

    callStatusVariant(log) {
      if (log._pending) return 'info'
      const s = (log.Status || log.status || '').toLowerCase().replace(/[\s_-]/g, '')
      if (s === 'completed') return 'success'
      if (s === 'noanswer' || s === 'notanswered' || s === 'missed' || s === 'busy') return 'warning'
      if (s === 'failed') return 'danger'
      return 'secondary'
    },

    callStatusLabel(log) {
      if (log._pending) return 'Initiated (pending)'
      const s = (log.Status || log.status || '').toLowerCase().replace(/[\s_-]/g, '')
      if (s === 'completed') return 'Answered'
      if (s === 'noanswer' || s === 'notanswered' || s === 'missed') return 'Not Answered'
      if (s === 'busy') return 'Busy'
      if (s === 'failed') return 'Failed'
      return log.Status || log.status || 'Unknown'
    },

    viewCandidateProfile() {
      if (!this.foundCandidate) return
      const route = this.$router.resolve({
        name: 'apps-candidates-view',
        params: { id: this.foundCandidate.id, jobId: this.jobId || 0 },
      })
      window.open(route.href, '_blank')
    },

    viewJob(jobId) {
      const route = this.$router.resolve({
        name: 'apps-view-job',
        params: { id: jobId },
      })
      window.open(route.href, '_blank')
    },

    closeModal() {
      this.$bvModal.hide('modal-outbound-dialer')
    },

    openWithPhone(phone) {
      const digits = toLocalIndianDigits(phone)
      this.phoneNumber = digits
      this._pendingAutoSearch = Boolean(digits)
    },

    viewCallHistoryInMyCalls() {
      const phone = toLocalIndianDigits(this.phoneNumber)
      if (!phone) return
      this.closeModal()
      this.$router.push({
        name: 'apps-call-analytics',
        query: { phone },
      })
    },

    resetModal() {
      this.step = 'dial'
      this.phoneNumber = ''
      this._pendingAutoSearch = false
      this.foundCandidate = null
      this.postCallStatus = null
      this.dupReasonSelected = null
      this.coolingTimeline = null
      this.dupReasonTouched = false
      this.coolingTimelineTouched = false
      this.notInterestedReasonSelected = null
      this.notInterestedReasonTouched = false
      this.isSavingQuickEdit = false
      this.quickEditOpen = false
      this.statusTouched = false
      this.callConnected = null
      this.newCandidate = {
        firstName: '', lastName: '', email: '', contact: '',
        companyName: '', jobTitle: '', selectedCity: '', ctc: '', exp: '',
        skillsSelected: '', qualification: '', dob: null, genderSelected: '',
        source: { title: 'ATS' }, expectedCTC: '', cvFile: null,
      }
      this.callLogs = []
      this.candidateJobs = []
      this.isLoadingLogs = false
      this.isLoadingJobs = false
      this.skippedCall = false
      this.wantsToAddAfterNoAnswer = false
      this.addErrors = { firstName: '', lastName: '', jobTitle: '', companyName: '', email: '', contact: '' }
    },

    onModalShow() {
      this.loadExotelConfig()
      this.refreshCallingAvailability()
      if (!this._availabilityUnsub) {
        this._availabilityUnsub = onAvailabilityChange(detail => {
          this.callsAvailable = detail.calls_available !== false
        })
      }
      this.newCandidate.contact = this.phoneNumber
      if (this.jobName && !this.newCandidate.skillsSelected) {
        this.newCandidate.skillsSelected = this.jobName
      }
      if (this._pendingAutoSearch && this.phoneValid) {
        this._pendingAutoSearch = false
        this.$nextTick(() => {
          this.searchInAts()
        })
      }
      if (this.qualifications.length === 0) {
        adminConfigService.getATSDropdownList().then(res => {
          if (res && res.data && res.data.data && res.data.data.qualificationList) {
            this.qualifications = res.data.data.qualificationList
          }
        }).catch(() => {})
      }
      if (Object.keys(this.recruiterMap).length === 0) {
        userService.getAllRecruitersList().then(res => {
          const list = Array.isArray(res) ? res : (res && res.data ? res.data : [])
          const map = {}
          list.forEach(r => {
            if (r.emp_id) {
              map[String(r.emp_id)] = r.emp_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || r.emp_id
            }
          })
          this.recruiterMap = map
        }).catch(() => {})
      }
    },

    onModalHide() {
      this.resetModal()
    },
  },
  beforeDestroy() {
    if (this._availabilityUnsub) {
      this._availabilityUnsub()
      this._availabilityUnsub = null
    }
  },
}
</script>

<style scoped>
/* ── Modal body padding (b-modal scrollable prop handles the height/scroll) ── */
.dialer-modal-body {
  padding: 1.25rem;
}

/* ── Utility classes extracted from repeated inline styles ── */
.min-w-0   { min-width: 0; }
.text-11   { font-size: 11px; }
.spinner-xs { width: 12px !important; height: 12px !important; }
.d-gap-8   { gap: 8px; }

.dialer-section {
  border-radius: 6px;
  overflow: hidden;
}
.dialer-section-header {
  background-color: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  display: flex;
  align-items: center;
}
.dark-layout .dialer-section-header {
  background-color: #283046;
  border-bottom-color: #3b4253;
}
.recent-dial-item:hover {
  background: rgba(0,0,0,0.04);
  border-radius: 6px;
  cursor: pointer;
}
/* Truncate long badge text (e.g. AssignedByMassMail) */
.badge-status-truncate {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}
/* Quick facts strip on candidate card */
.dialer-quick-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  border-top: 1px solid #ebe9f1;
  padding-top: 8px;
}
.dark-layout .dialer-quick-facts {
  border-top-color: #3b4253;
}
.dialer-fact-item {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #6e6b7b;
  white-space: nowrap;
}

/* ── v-select dropdown: always above the modal overlay ── */
:deep(.vs__dropdown-menu) {
  z-index: 9999 !important;
}

/* ── Status / duplicate select dropdown: cap height so it never overflows the viewport ── */
.dialer-status-select :deep(.vs__dropdown-menu) {
  max-height: 220px;
  overflow-y: auto;
}

/* ── Native form selects in post-call step (duplicate / cooling / not interested) ── */
.dialer-form-select {
  min-height: 38px;
}

/* ── Update Status group in post-call step ── */
.dialer-status-group {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.dialer-status-group label {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: block;
}
.dialer-status-select :deep(.vs__dropdown-toggle) {
  padding: 0.375rem 0.5rem;
  border-radius: 0.357rem;
  min-height: 38px;
}
.dialer-status-select :deep(.vs__selected) {
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 2px 4px;
  margin: 2px 2px 0;
}
.dialer-status-select :deep(.vs__dropdown-option) {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  white-space: normal;
  overflow: visible;
}

/* ── Profile Share inline form ── */
.dialer-profile-share {
  background: #f6fff8;
  border: 1px solid #d4edda;
  border-radius: 0.4rem;
  padding: 0.75rem;
}
.dialer-profile-share .x-small {
  font-size: 0.75rem;
}
.dialer-profile-share .form-group {
  margin-bottom: 0.5rem !important;
}
</style>
